<script setup>
import { ref, onMounted, reactive, computed, watch, nextTick, } from 'vue'
import GenericCollapse from './GenericCollapse.vue';

const props = defineProps({
  stepNumber: {
    type: Number,
    default: 1,
  },
  state: {
    type: Object,
    required: true,
  }
});

let title = computed(() => {
  return props.state.steps[props.stepNumber - 1].name;
});

let isDone = computed(() => {
  return props.state.isStepDone(props.stepNumber);
});

let isReady = computed(() => {
  return props.state.isStepReady(props.stepNumber);
});

let isCurrentStep = computed(() => {
  return props.stepNumber === props.state.currentStep;
});

// Determine if this step is available (previous steps are completed)
let isAvailable = computed(() => {
  // Step 1 is always available
  if (props.stepNumber === 1) return true;
  
  // For other steps, check if the previous step is done
  return props.stepNumber <= props.state.currentStep;
});

function onMarkDone() {
  props.state.markStepDone(props.stepNumber);
}

const collapseRef = ref(null);

// Watch for changes to currentStep and automatically open/close the collapse
watch(() => props.state.currentStep, (newVal) => {
  if (newVal === props.stepNumber && collapseRef.value) {
    // This is the current step, open it
    collapseRef.value.open();
  } else if (collapseRef.value) {
    // This is not the current step, close it
    collapseRef.value.close();
  }
}, { immediate: true });

// Also watch on mount to ensure proper initial state
onMounted(() => {
  if (isCurrentStep.value && collapseRef.value) {
    collapseRef.value.open();
  }
});

</script>

<template>
  <div>
    <GenericCollapse ref="collapseRef" class="GuideStep" :disabled="!isAvailable" :class="{isDone: isDone, isCurrentStep: isCurrentStep, isLocked: !isAvailable}">
      <template v-slot:title>
        <div class="StepTitleContainer">
          <div class="StepNumberBadge">{{ stepNumber }}</div>
          <div class="StepTitleText">
            <span class="StepTitle">{{title}}</span>
          </div>
          <div v-if="isDone" class="StepStatusIcon">
            <i class="bi-check-circle-fill"></i>
          </div>
          <div v-else-if="!isAvailable" class="StepStatusIcon">
            <i class="bi-lock-fill"></i>
          </div>
        </div>
      </template>
      <template v-slot:content>
        <slot name="content"></slot>
        <div class="DoneSection mt-m">
          <button v-if="!isDone" class="MarkDoneButton" :disabled="!isReady || !isAvailable" @click="onMarkDone">
            <i class="bi-check-circle mr-xs"></i> Mark Done
          </button>
          <p v-else class="Bold f-l pc TextAlignCenter"><i class="bi-check-circle-fill mr-xxs"></i>Step Complete!</p>
        </div>
      </template>
    </GenericCollapse>
  </div>
</template>

<style scoped>
.GuideStep {
}

.GuideStep.isCurrentStep:deep(.CollapseBtn) {
  background-color: var(--pc);
  color: white;
  border: 1px solid var(--pc);
}

.GuideStep.isCurrentStep:deep(.CollapseBtn:hover) {
  background-color: var(--pc-highlight);
}

.GuideStep.isCurrentStep:deep(.StepNumberBadge) {
  background-color: white;
  color: var(--pc);
  border: 1px solid white;
}

.GuideStep.isCurrentStep:deep(.StepTitle) {
  color: white;
}

.GuideStep.isDone:deep(.CollapseBtn) {
  background-color: var(--pc-lightest);
  border-color: var(--pc-light);
}

.GuideStep.isLocked:deep(.CollapseBtn) {
  background-color: var(--grey-95);
  color: var(--grey-40);
  border-color: var(--grey-80);
  cursor: not-allowed;
}

.StepTitleContainer {
  display: flex;
  align-items: center;
  width: 100%;
}

.StepNumberBadge {
  background-color: var(--pc);
  color: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: var(--s);
  flex-shrink: 0;
  font-size: var(--f-s);
}

.StepTitleText {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-grow: 1;
}

.StepTitle {
  font-size: var(--f-l);
  font-weight: bold;
  line-height: 1.2;
}

.StepStatusIcon {
  margin-left: var(--xs);
  font-size: var(--f-l);
}

.StepStatusIcon i {
  vertical-align: middle;
}

.isDone .StepStatusIcon {
  color: var(--g-30, #27ae60);
}

.isLocked .StepStatusIcon {
  color: var(--grey-50);
}

.GuideStep.isLocked:deep(.StepNumberBadge) {
  background-color: var(--grey-60);
  color: var(--grey-90);
  opacity: 0.7;
}

.GuideStep.isDone:deep(.StepNumberBadge) {
  background-color: var(--pc-dark);
}

.DoneSection {
  display: flex;
  justify-content: center;
  margin-top: var(--l);
  margin-bottom: var(--s);
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.GuideStep.isCurrentStep:deep(.DoneSection) {
  animation: pulse 2s infinite;
}

.GuideStep.isCurrentStep:deep(.DoneSection:has(.MarkDoneButton:disabled)) {
  animation: none;
}

.MarkDoneBtn {
  margin: auto;
}
</style>
